import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  FolderOpen, 
  Calendar,
  User,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  LayoutGrid,
  List
} from 'lucide-react';

import { dossiersData, enqueteursData } from '../../data/mockData';

const DossiersListing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [enqueteurFilter, setEnqueteurFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [prioriteFilter, setPrioriteFilter] = useState('');
  const [sortField, setSortField] = useState('dateCreation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'grid'

  // Filtrage et tri des dossiers
  const filteredAndSortedDossiers = useMemo(() => {
    let filtered = dossiersData.filter(dossier => {
      const matchesSearch = 
        dossier.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.assure.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.assure.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.typeSinistre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dossier.lieu.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatut = !statutFilter || dossier.statut === statutFilter;
      const matchesEnqueteur = !enqueteurFilter || dossier.enqueteurId === parseInt(enqueteurFilter);
      const matchesType = !typeFilter || dossier.typeSinistre === typeFilter;
      const matchesPriorite = !prioriteFilter || dossier.priorite === prioriteFilter;
      
      return matchesSearch && matchesStatut && matchesEnqueteur && matchesType && matchesPriorite;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'dateCreation' || sortField === 'dateSinistre') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, statutFilter, enqueteurFilter, typeFilter, prioriteFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'nouveau': 'badge-info',
      'refuse': 'badge-danger',
      'attribue': 'badge-info',
      'en_cours': 'badge-warning',
      'cloture': 'badge-success'
    };
    
    const labels = {
      'nouveau': 'Nouveau',
      'refuse': 'Refusé',
      'attribue': 'Attribué',
      'en_cours': 'En cours',
      'cloture': 'Clôturé'
    };

    return { class: badges[statut], label: labels[statut] };
  };

  const getPrioriteColor = (priorite) => {
    const colors = {
      'basse': 'text-atl-green-600',
      'normale': 'text-port-gray-600',
      'haute': 'text-red-600'
    };
    return colors[priorite] || 'text-port-gray-600';
  };

  const uniqueTypes = [...new Set(dossiersData.map(dossier => dossier.typeSinistre))];

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-port-gray-900">Gestion des Dossiers</h1>
          <p className="text-port-gray-600 mt-1">
            {filteredAndSortedDossiers.length} dossier{filteredAndSortedDossiers.length > 1 ? 's' : ''} trouvé{filteredAndSortedDossiers.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Toggle view mode */}
          <div className="flex bg-port-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'table' ? 'bg-white shadow-sm text-atl-blue-600' : 'text-port-gray-600'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' ? 'bg-white shadow-sm text-atl-blue-600' : 'text-port-gray-600'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          
          <Link to="/admin/dossiers/nouveau" className="btn btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau Dossier
          </Link>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-port-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par référence, client, assuré, type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            {/* Bouton filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
              {(statutFilter || enqueteurFilter || typeFilter || prioriteFilter) && (
                <span className="ml-2 bg-atl-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {[statutFilter, enqueteurFilter, typeFilter, prioriteFilter].filter(f => f).length}
                </span>
              )}
            </button>
          </div>

          {/* Filtres étendus */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-port-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="form-label">Statut</label>
                  <select
                    value={statutFilter}
                    onChange={(e) => setStatutFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="ouvert">Ouvert</option>
                    <option value="attribue">Attribué</option>
                    <option value="en_cours">En cours</option>
                    <option value="cloture">Clôturé</option>
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Enquêteur</label>
                  <select
                    value={enqueteurFilter}
                    onChange={(e) => setEnqueteurFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les enquêteurs</option>
                    <option value="">Non attribué</option>
                    {enqueteursData.map(enqueteur => (
                      <option key={enqueteur.id} value={enqueteur.id}>{enqueteur.nom}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Type de sinistre</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les types</option>
                    {uniqueTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setStatutFilter('');
                      setEnqueteurFilter('');
                      setTypeFilter('');
                      setPrioriteFilter('');
                    }}
                    className="btn btn-secondary w-full"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Liste/Grille des dossiers */}
      {viewMode === 'table' ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th 
                    className="cursor-pointer hover:bg-port-gray-100"
                    onClick={() => handleSort('reference')}
                  >
                    Référence {getSortIcon('reference')}
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-port-gray-100"
                    onClick={() => handleSort('client')}
                  >
                    <div className="flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      Client {getSortIcon('client')}
                    </div>
                  </th>
                  <th>Assuré</th>
                  <th>Type / Nature</th>
                  <th 
                    className="cursor-pointer hover:bg-port-gray-100"
                    onClick={() => handleSort('dateCreation')}
                  >
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Date création {getSortIcon('dateCreation')}
                    </div>
                  </th>
                  <th>Enquêteur</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedDossiers.map(dossier => {
                  const statutBadge = getStatutBadge(dossier.statut);
                  return (
                    <tr key={dossier.id} className="hover:bg-port-gray-50">
                      <td>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/dossiers/1`}
                            className="p-2 text-atl-blue-600 hover:bg-atl-blue-100 rounded-lg transition-colors"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <button
                            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="font-medium text-atl-blue-600">
                        {dossier.reference}
                      </td>
                      <td>
                        <Link to={`/admin/clients/1`} className="text-port-gray-900 hover:text-atl-blue-600">
                          {dossier.client}
                        </Link>
                      </td>
                      <td>
                        <div>
                          <div className="font-medium text-port-gray-900">
                            {dossier.assure.prenom} {dossier.assure.nom}
                          </div>
                          <div className="text-sm text-port-gray-500">
                            {dossier.assure.telephone}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="font-medium text-port-gray-900">
                            {dossier.typeSinistre}
                          </div>
                          <div className="text-sm text-port-gray-500">
                            {dossier.natureSinistre}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="text-port-gray-900">
                            {new Date(dossier.dateCreation).toLocaleDateString('fr-FR')}
                          </div>
                          <div className="text-sm text-port-gray-500">
                            Sinistre: {new Date(dossier.dateSinistre).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </td>
                      <td>
                        {dossier.enqueteur ? (
                          <div className="flex items-center">
                            <div className="w-6 h-6 bg-atl-blue-100 rounded-full flex items-center justify-center mr-2">
                              <User className="h-3 w-3 text-atl-blue-600" />
                            </div>
                            <span className="text-sm text-port-gray-900">{dossier.enqueteur}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-port-gray-500 italic">Non attribué</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statutBadge.class}`}>
                          {statutBadge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredAndSortedDossiers.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-port-gray-900 mb-2">
                Aucun dossier trouvé
              </h3>
              <p className="text-port-gray-500">
                Essayez de modifier vos critères de recherche ou créez un nouveau dossier.
              </p>
            </div>
          )}
        </div>
      ) : (
        // Vue grille
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedDossiers.map(dossier => {
            const statutBadge = getStatutBadge(dossier.statut);
            return (
              <div key={dossier.id} className="card hover:shadow-lg transition-shadow">
                <div className="card-body">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium text-atl-blue-600">{dossier.reference}</h3>
                    <span className={`badge ${statutBadge.class}`}>
                      {statutBadge.label}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                      <Building className="h-4 w-4 text-port-gray-400 mr-2" />
                      <span className="text-port-gray-900">{dossier.client}</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <User className="h-4 w-4 text-port-gray-400 mr-2" />
                      <span className="text-port-gray-900">
                        {dossier.assure.prenom} {dossier.assure.nom}
                      </span>
                    </div>
                    
                    <div className="text-sm">
                      <span className="font-medium text-port-gray-900">{dossier.typeSinistre}</span>
                      <span className="text-port-gray-500"> - {dossier.natureSinistre}</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <Calendar className="h-4 w-4 text-port-gray-400 mr-2" />
                      <span className="text-port-gray-500">
                        {new Date(dossier.dateCreation).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-sm">
                      {dossier.enqueteur ? (
                        <span className="text-port-gray-900">{dossier.enqueteur}</span>
                      ) : (
                        <span className="text-port-gray-500 italic">Non attribué</span>
                      )}
                    </div>
                    
                    <Link
                      to={`/admin/dossiers/${dossier.id}`}
                      className="btn btn-primary btn-sm"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DossiersListing;
